/**
 *
 * @module tokenClient
 * @desc Provides a data client that can be used to access token services.
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/token.md
 * @see https://github.bamtech.co/api-services-eng/token-service/blob/master/doc/api/WEB.md
 * @see https://github.bamtech.co/api-services-eng/token-service/tree/master/doc/api/token-exchange
 * @see https://github.bamtech.co/api-services-eng/token-service/blob/master/doc/api/token-exchange/ACCESS-TOKEN.md
 * @see https://tools.ietf.org/html/rfc6749
 * @see https://jwt.io/ (decode, verify and generate JSON Web Tokens)
 *
 */

/* eslint-disable quote-props */
/* eslint-disable camelcase */

import queryString from 'query-string';
import Check from 'check-types';
import Logging from './../../logging/entry';
import Exception from './../exception/entry';
import HttpClient from './../platforms/httpClient';
import AccessContext from './accessContext';
import TokenRequest from './tokenRequest';
import TokenClientEndpoint from './tokenClientEndpoint';
import TokenClientConfiguration from './tokenClientConfiguration';
import Util from './../util/entry';
import DustLogUtility from './../internal/dust/dustLogUtility';
import DustUrnReference from './../internal/dust/dustUrnReference';
import Configuration from './../configuration/entry';

const InvalidArgumentException = Exception.InvalidArgumentException;
const ServiceException = Exception.ServiceException;

const HttpMethod = Configuration.HttpMethod;
const TokenClientDustUrnReference = DustUrnReference.services.token.tokenClient;

const {
    nonEmptyString,
    instanceStrict
} = Check.assert;

/**
 *
 * @class TokenClient
 * @implements {ITokenClient}
 * @desc Stateless client object that is capable of calling the service to
 * exchange a TokenRequest for an AccessContext.
 *
 */
export default class TokenClient {

    /**
     *
     * @param {TokenClientConfiguration} tokenClientConfiguration
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @param {IHttpClient} [httpClient=null]
     * @throws {InvalidArgumentException}
     *
     */
    constructor(tokenClientConfiguration, logger = Logging, httpClient = null) {

        const errorMessage = {
            ctor: 'TokenClient(tokenClientConfiguration, logger, httpClient)',
            get tokenClientConfiguration() {
                return `${this.ctor} needs TokenClientConfiguration instance`;
            }
        };

        instanceStrict(
            tokenClientConfiguration,
            TokenClientConfiguration,
            errorMessage.tokenClientConfiguration,
            InvalidArgumentException
        );

        /**
         *
         * @access private
         * @type {TokenClientConfiguration}
         * @desc The configuration information to use.
         *
         */
        this.config = tokenClientConfiguration;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {HttpClient}
         * @desc The object responsible for making HTTP requests.
         *
         */
        this.httpClient = httpClient || new HttpClient(logger);

        /**
         *
         * @access private
         * @type {Object}
         * @desc Utility Object for storing platform data.
         *
         */
        this.platformData = {
            platform: this.config.extras.platformId
        };

        /**
         *
         * @access private
         * @type {Boolean}
         * @note needs to default to true to collect dust events before the configuration is fetched and we can
         * determine if this should be enabled
         *
         */
        this.dustEnabled = true;

        this.logger.log(TokenClient.name, 'Created.');

    }

    /**
     *
     * @access public
     * @param {TokenRequest} tokenRequest - An object that contains the
     * information required for making an exchange request.
     * @param {String} apiKey - The API key to provide as authorization for the request.
     * @desc Attempts to exchange a token for an access token.
     * @returns {Promise<AccessContext>} Result indicating activation success and any active entitlements.
     *
     */
    exchange(tokenRequest, apiKey) {

        const errorMessage = {
            method: 'TokenClient.exchange(tokenRequest, apiKey)',
            get tokenRequest() {
                return `${this.method} needs TokenRequest instance`;
            },
            get apiKey() {
                return `${this.method} apiKey should be a String with length`;
            }
        };

        instanceStrict(tokenRequest, TokenRequest, errorMessage.tokenRequest, InvalidArgumentException);
        nonEmptyString(apiKey, errorMessage.apiKey, InvalidArgumentException);

        const { dustEnabled, httpClient, logger, platformData } = this;
        const { grantType, tokenData, latitude, longitude } = tokenRequest;

        const payload = this.getPayload(
            apiKey,
            TokenClientEndpoint.exchange,
            Object.assign(
                {
                    grant_type: grantType,
                    latitude,
                    longitude
                },
                tokenData,
                platformData
            )
        );

        logger.info(TokenClient.name, `Exchange token request:
            originating grantType: "${tokenData.subject_token_type}",
            latitude: "${latitude}",
            longitude: "${longitude}",
            platform: "${platformData.platform}"
        `.replace(/(\s(?=\s))/g, ''));

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: TokenClient.name,
            urn: TokenClientDustUrnReference.exchange,
            payload,
            method: HttpMethod.POST
        });

        return httpClient.post(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).then((response) => {

            const { data } = response;
            const { access_token, token_type, refresh_token, expires_in } = data;

            /**
             *
             * access_token - The access token issued by the authorization server.
             * token_type - The type of the Access Token. Will always be bearer.
             * expires_in - The lifetime in seconds of the Access Token. Also known as the Access Token Expiration.
             * refresh_token - The Refresh Token, which can be used to obtain a new Access Token.
             *
             */
            return Promise.resolve(
                new AccessContext({
                    token: access_token,
                    tokenType: token_type,
                    refreshToken: refresh_token,
                    expiresIn: expires_in
                })
            );

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /* #region private: */

    /**
     *
     * @access private
     * @param {String} apiKey
     * @param {TokenClientEndpoint} rel
     * @param {Object} body
     * @returns {Object} The payload for the client call.
     *
     */
    getPayload(apiKey, rel, body) {

        const { endpoints } = this.config;
        const endpoint = endpoints[rel];
        const { href, headers } = endpoint;
        const requestBody = body ? queryString.stringify(body) : '';
        const requestHeaders = Object.assign({}, headers);

        requestHeaders.Authorization = requestHeaders.Authorization.replace(/\{apiKey\}/gi, apiKey);

        return {
            url: href,
            body: requestBody,
            headers: requestHeaders
        };

    }

}



// WEBPACK FOOTER //
// ./src/services/token/tokenClient.js